import React from "react";
import { Link } from "react-router-dom";
import {
 ArrowUpRight, Nut, Sprout, Factory, Handshake, Truck, ShieldCheck,
 Globe, Package, Check, ChevronDown
} from "lucide-react";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { useT } from "@/i18n/helpers";
import Seo from "@/components/Seo";

const Section = ({ children, className = "", id }) => (
 <section id={id} className={`border-t border-nanda-line ${className}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">{children}</div>
 </section>
);
const Overline = ({ children }) => (<div className="label-overline text-nanda-muted mb-4">{children}</div>);

export default function FiliereCajou() {
 const tt = useT();

 const CHAINE = [
 { i: Sprout, t: tt("Connaissance de la filière", "Value chain knowledge", "Conhecimento da fileira"),
 d: tt("Cartographie des zones de production, calendriers, qualités, acteurs de terrain en Guinée-Bissau.",
 "Mapping of production areas, calendars, qualities and field actors in Guinea-Bissau.",
 "Cartografia das zonas de produção, calendários, qualidades, actores de terreno na Guiné-Bissau.") },
 { i: Handshake, t: tt("Sourcing & mise en relation", "Sourcing & matchmaking", "Sourcing e mise en relation"),
 d: tt("Identification de producteurs, coopératives et transformateurs sérieux. Intermédiation avec des acheteurs internationaux.",
 "Identification of serious producers, cooperatives and processors. Intermediation with international buyers.",
 "Identificação de produtores, cooperativas e transformadores sérios. Intermediação com compradores internacionais.") },
 { i: Factory, t: tt("Accompagnement de la transformation locale", "Local processing support", "Acompanhamento da transformação local"),
 d: tt("Appui à des projets de décorticage, calibrage, torréfaction, conditionnement et création de valeur en Afrique.",
 "Support for hulling, grading, roasting, packaging projects and value creation in Africa.",
 "Apoio a projectos de descasque, calibragem, torrefacção, embalagem e criação de valor em África.") },
 { i: Package, t: tt("Conditionnement & valorisation", "Packaging & valorisation", "Embalagem e valorização"),
 d: tt("Cahiers des charges, formats retail, coffrets, packaging travaillé pour l'export.",
 "Specifications, retail formats, gift boxes, refined export packaging.",
 "Cadernos de encargos, formatos retalho, caixas, embalagem trabalhada para exportação.") },
 { i: Truck, t: tt("Logistique & export", "Logistics & export", "Logística e exportação"),
 d: tt("Coordination avec les port autonome de Bissau, groupage, documents douaniers, incoterms.",
 "Coordination with the ports of Bissau and consolidation, customs documents, incoterms.",
 "Coordenação com os porto autónomo de Bissau, consolidação, documentos aduaneiros, incoterms.") },
 { i: Globe, t: tt("Développement de débouchés", "Market development", "Desenvolvimento de saídas comerciais"),
 d: tt("Accès aux réseaux d'acheteurs en Europe, Amérique du Nord et Moyen-Orient, foires, salons professionnels.",
 "Access to buyer networks in Europe, North America and the Middle East, trade fairs and industry events.",
 "Acesso a redes de compradores na Europa, América do Norte e Médio Oriente, feiras e salões profissionais.") },
 ];

 const PRODUITS = [
 { t: tt("Noix de cajou brute (RCN)", "Raw cashew nuts (RCN)", "Castanha de caju em bruto (RCN)"),
 d: tt("Récolte artisanale et industrielle, calibrages et conditionnements pour l'export.",
 "Artisanal and industrial harvest, grades and packaging for export.",
 "Colheita artesanal e industrial, calibrações e embalagens para exportação.") },
 { t: tt("Amande de cajou", "Cashew kernel", "Amêndoa de caju"),
 d: tt("Amandes blanches, entières ou brisées, calibrées selon les standards internationaux.",
 "White whole or broken kernels, graded to international standards.",
 "Amêndoas brancas, inteiras ou partidas, calibradas segundo os padrões internacionais.") },
 { t: tt("Amandes transformées", "Processed kernels", "Amêndoas transformadas"),
 d: tt("Grillées, salées, natures, aromatisées. Formats vrac et retail.",
 "Roasted, salted, plain, flavoured. Bulk and retail formats.",
 "Torradas, salgadas, naturais, aromatizadas. Formatos a granel e retalho.") },
 { t: tt("Sous-produits & valorisation", "By-products & valorisation", "Subprodutos e valorização"),
 d: tt("Sous-produits de calibrage, brisures, valorisation en pâtes et beurres, débouchés cosmétiques.",
 "Grading by-products, broken kernels, valorisation into pastes and butters, cosmetic outlets.",
 "Subprodutos de calibragem, quebras, valorização em pastas e manteigas, saídas cosméticas.") },
 ];

 const BESOINS = [
 tt("Équipements de décorticage, calibrage et conditionnement", "Hulling, grading and packaging equipment", "Equipamentos de descasque, calibragem e embalagem"),
 tt("Emballages primaires et secondaires adaptés à l'export", "Primary and secondary packaging suited to export", "Embalagens primárias e secundárias adaptadas à exportação"),
 tt("Énergie stable pour les unités de transformation", "Stable energy for processing units", "Energia estável para as unidades de transformação"),
 tt("Stockage sec, ventilé et sécurisé", "Dry, ventilated and secure storage", "Armazenamento seco, ventilado e seguro"),
 tt("Logistique et coordination portuaire", "Logistics and port coordination", "Logística e coordenação portuária"),
 tt("Financement des campagnes de collecte", "Financing of collection campaigns", "Financiamento das campanhas de recolha"),
 ];

 const OPPORTUNITES = [
 tt("Ajouter de la valeur en Afrique plutôt que d'exporter la matière brute.", "Add value in Africa rather than exporting the raw material.", "Acrescentar valor em África em vez de exportar a matéria em bruto."),
 tt("Développer des marques ouest-africaines d'amandes premium.", "Develop premium West African kernel brands.", "Desenvolver marcas oeste-africanas de amêndoas premium."),
 tt("Structurer une traçabilité par coopérative et par lot.", "Structure traceability by cooperative and by batch.", "Estruturar uma rastreabilidade por cooperativa e por lote."),
 tt("Créer des débouchés stables pour les producteurs.", "Create stable outlets for producers.", "Criar saídas comerciais estáveis para os produtores."),
 tt("Encourager les femmes et les jeunes dans les métiers de la transformation.", "Encourage women and youth in processing trades.", "Encorajar as mulheres e os jovens nas profissões da transformação."),
 tt("Développer des filières bio, équitables et durables.", "Develop organic, fair and sustainable value chains.", "Desenvolver fileiras bio, equitativas e sustentáveis."),
 ];

 return (
 <div data-testid="page-filiere-cajou">
 <Seo />
 <section className="relative h-[92vh] min-h-[600px] w-full overflow-hidden bg-nanda-ink">
 <img src="/photos/sector-cajou-hero.jpg" alt={tt("Filière cajou en Afrique de l'Ouest, sourcing et valorisation par NANDA SÁ", "Cashew value chain in West Africa, sourcing and valorisation by NANDA SÁ", "Fileira do caju na África Ocidental, sourcing e valorização pela NANDA SÁ")} className="absolute inset-0 h-full w-full object-cover opacity-70" loading="eager" />
 <div className="absolute inset-0 bg-gradient-to-b from-nanda-ink/50 via-nanda-ink/25 to-nanda-ink/80" />
 <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-24 md:pb-32">
 <FadeUp>
 <div className="label-overline text-white/85 mb-6">{tt("Filière cajou · Guinée-Bissau", "Cashew value chain · Guinea-Bissau", "Fileira do caju · Guiné-Bissau")}</div>
 <h1 className="font-serif text-white text-5xl md:text-7xl lg:text-[88px] leading-[1.02] max-w-5xl">
 {tt("La filière cajou,", "The cashew value chain,", "A fileira do caju,")}<br />
 <span className="text-nanda-gold italic">{tt("de la parcelle à l'international.", "from plot to international markets.", "da parcela ao mercado internacional.")}</span>
 </h1>
 <p className="mt-8 max-w-2xl text-white/85 text-lg md:text-xl leading-relaxed">
 {tt("NANDA SÁ accompagne la filière cajou d'Afrique de l'Ouest : sourcing, valorisation, transformation locale, développement de débouchés et mise en relation entre producteurs, transformateurs et acheteurs internationaux.",
 "NANDA SÁ supports the West African cashew value chain: sourcing, valorisation, local processing, market development and matchmaking between producers, processors and international buyers.",
 "A NANDA SÁ acompanha a fileira do caju da África Ocidental: sourcing, valorização, transformação local, desenvolvimento de saídas comerciais e mise en relation entre produtores, transformadores e compradores internacionais.")}
 </p>
 <div className="mt-12 flex flex-wrap gap-4">
 <Link to="/contact?subject=Filiere-cajou" className="inline-flex items-center gap-3 bg-white text-nanda-ink px-8 py-4 label-overline hover:bg-nanda-gold hover:text-white transition-colors duration-500" data-testid="cajou-cta-contact">
 {tt("Discuter d'un projet cajou", "Discuss a cashew project", "Discutir um projecto de caju")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 <a href="#chaine" className="inline-flex items-center gap-3 border border-white/50 text-white px-8 py-4 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="cajou-cta-chaine">
 {tt("Voir la chaîne complète", "See the full chain", "Ver a cadeia completa")} <ChevronDown size={16} strokeWidth={1.5} />
 </a>
 </div>
 </FadeUp>
 </div>
 </section>

 <Section>
 <div className="grid grid-cols-1 md:grid-cols-12 gap-14">
 <FadeUp className="md:col-span-5">
 <Overline>{tt("Le contexte", "The context", "O contexto")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Une filière stratégique, encore largement à structurer.", "A strategic value chain, still largely to be structured.", "Uma fileira estratégica, ainda largamente por estruturar.")}
 </h2>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-7 text-nanda-inkSoft text-[17px] leading-relaxed space-y-5">
 <p>{tt("L'anacardier fait partie du paysage de la Guinée-Bissau. La noix de cajou est l'un des principaux produits d'exportation de la région, mais elle quitte encore majoritairement le territoire sans transformation, ce qui laisse la valeur ajoutée aux transformateurs asiatiques.",
 "The cashew tree is part of the landscape of Guinea-Bissau. The cashew nut is one of the region's main export products, but it still mostly leaves the territory unprocessed, leaving the added value to Asian processors.",
 "O cajueiro faz parte da paisagem da Guiné-Bissau. A castanha de caju é um dos principais produtos de exportação da região, mas continua a sair maioritariamente do território sem transformação, deixando o valor acrescentado aos transformadores asiáticos.")}</p>
 <p>{tt("NANDA SÁ n'affirme ni exporter de grands volumes ni posséder ses propres unités industrielles. Notre positionnement est clair : nous sommes un acteur d'accompagnement, de mise en relation et de développement de projets sur toute la chaîne de valeur.",
 "NANDA SÁ neither claims to export large volumes nor to own its own industrial units. Our positioning is clear: we are a player of support, matchmaking and project development along the entire value chain.",
 "A NANDA SÁ não afirma exportar grandes volumes nem possuir as suas próprias unidades industriais. O nosso posicionamento é claro: somos um actor de acompanhamento, de mise en relation e de desenvolvimento de projectos em toda a cadeia de valor.")}</p>
 <p>{tt("C'est en travaillant avec les coopératives, les transformateurs, les acheteurs et les institutions que la filière ouest-africaine pourra structurer une meilleure valorisation locale et un accès plus juste aux marchés internationaux.",
 "It is by working with cooperatives, processors, buyers and institutions that the West African value chain will be able to structure better local valorisation and fairer access to international markets.",
 "É trabalhando com as cooperativas, os transformadores, os compradores e as instituições que a fileira oeste-africana poderá estruturar uma melhor valorização local e um acesso mais justo aos mercados internacionais.")}</p>
 </FadeUp>
 </div>
 </Section>

 <Section id="chaine" className="bg-nanda-bgAlt">
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Notre chaîne complète", "Our full chain", "A nossa cadeia completa")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Six maillons, une seule filière.", "Six links, one single chain.", "Seis elos, uma única fileira.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {CHAINE.map((c) => (
 <StaggerItem key={c.t} className="bg-white p-8 md:p-10">
 <c.i size={28} strokeWidth={1.2} className="text-nanda-terracotta mb-5" />
 <div className="font-serif text-xl md:text-2xl text-nanda-ink leading-tight">{c.t}</div>
 <p className="mt-4 text-sm text-nanda-inkSoft leading-relaxed">{c.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 <Section>
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Produits que nous suivons", "Products we follow", "Produtos que acompanhamos")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Quatre familles de produits.", "Four product families.", "Quatro famílias de produtos.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-nanda-line border border-nanda-line">
 {PRODUITS.map((p) => (
 <StaggerItem key={p.t} className="bg-nanda-bgAlt p-8 md:p-10">
 <Nut size={22} strokeWidth={1.2} className="text-nanda-ink mb-4" />
 <div className="font-serif text-xl text-nanda-ink leading-tight">{p.t}</div>
 <p className="mt-3 text-sm text-nanda-inkSoft leading-relaxed">{p.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 <Section className="bg-nanda-bgAlt">
 <FadeUp className="mb-12 max-w-3xl">
 <Overline>{tt("Besoins concrets de la filière", "Concrete value chain needs", "Necessidades concretas da fileira")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Où l'investissement peut faire la différence.", "Where investment can make a difference.", "Onde o investimento pode fazer a diferença.")}
 </h2>
 </FadeUp>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
 {BESOINS.map((b) => (
 <div key={b} className="flex items-start gap-3 py-4 border-t border-nanda-line">
 <ShieldCheck size={18} strokeWidth={1.5} className="text-nanda-terracotta mt-1 shrink-0" />
 <span className="text-[15px] text-nanda-ink leading-snug">{b}</span>
 </div>
 ))}
 </div>
 </Section>

 <Section>
 <FadeUp className="mb-12 max-w-3xl">
 <Overline>{tt("Opportunités de création de valeur", "Value creation opportunities", "Oportunidades de criação de valor")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Six leviers concrets.", "Six concrete levers.", "Seis alavancas concretas.")}
 </h2>
 </FadeUp>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
 {OPPORTUNITES.map((o) => (
 <div key={o} className="flex items-start gap-3 py-4 border-t border-nanda-line">
 <Check size={18} strokeWidth={2} className="text-nanda-terracotta mt-1 shrink-0" />
 <span className="text-sm md:text-[15px] text-nanda-ink leading-snug">{o}</span>
 </div>
 ))}
 </div>
 </Section>

 <Section className="bg-nanda-terracotta text-white">
 <FadeUp className="max-w-4xl mx-auto text-center">
 <Overline><span className="text-nanda-gold">{tt("Ensemble sur la filière cajou", "Together on the cashew value chain", "Juntos na fileira do caju")}</span></Overline>
 <h3 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.08] mt-4">
 {tt("Producteur, acheteur,", "Producer, buyer,", "Produtor, comprador,")}<br />
 <span className="text-nanda-gold italic">{tt("investisseur ou institution ?", "investor or institution?", "investidor ou instituição?")}</span>
 </h3>
 <p className="mt-8 text-white/80 text-lg leading-relaxed max-w-2xl mx-auto">
 {tt("Décrivez votre positionnement et votre projet. Nous vous répondons personnellement et nous explorons ensemble comment nous pouvons créer de la valeur, ensemble.",
 "Describe your positioning and your project. We reply personally and explore together how we can create value.",
 "Descreva o seu posicionamento e o seu projecto. Respondemos-lhe pessoalmente e exploramos juntos como podemos criar valor.")}
 </p>
 <div className="mt-12 flex flex-wrap gap-4 justify-center">
 <Link to="/contact?subject=Filiere-cajou" className="inline-flex items-center gap-3 bg-nanda-gold text-white px-10 py-5 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="cajou-cta-final">
 {tt("Nous écrire", "Write to us", "Escreva-nos")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 <Link to="/matieres-premieres-naturelles" className="inline-flex items-center gap-3 border border-white/50 text-white px-10 py-5 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="cajou-cta-mp">
 {tt("Voir toutes les matières", "See all raw materials", "Ver todas as matérias")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 </div>
 </FadeUp>
 </Section>
 </div>
 );
}
